import React from 'react';
import { Link } from 'react-router-dom';
import { useGuidesBase } from '../../hooks/useGuidesBase';
import { Box, Title, Paper, Text, Stack, Group, Badge, Anchor, Alert, ThemeIcon, Table, Breadcrumbs, Stepper } from '@mantine/core';
import { Lightbulb, Check, Warning, Users, ShoppingCart, Globe, Eye } from '@phosphor-icons/react';
import SEO from '../../components/SEO';

export default function RemarketingGuide() {
  const guidesBase = useGuidesBase();
  return (
    <Box px="lg" py="md">
      <SEO
        title="Remarketing i listy odbiorców w Google Ads"
        description="RLSA, remarketing dynamiczny, Customer Match. Jak docierać do osób, które już odwiedziły Twoją stronę i zwiększyć ROAS."
        path={`${guidesBase}/remarketing`}
        noIndex={guidesBase !== '/poradniki'}
      />
      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Remarketing i listy odbiorców</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">Remarketing i listy odbiorców</Title>

      <Alert color="blue" mb="md" icon={<Lightbulb size={16} />}>
        <Text size="sm">Remarketing pozwala dotrzeć do osób, które już odwiedziły Twoją stronę. To najwyższy ROAS ze wszystkich typów kampanii, bo targetujesz ludzi, którzy już znają Twoją ofertę.</Text>
      </Alert>

      {/* Types */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Typy remarketingu</Text>
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Typ</Table.Th>
              <Table.Th>Opis</Table.Th>
              <Table.Th>ROAS</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {[
              ['RLSA (Search)', 'Wyższe stawki w Search dla osób, które odwiedziły stronę', 'Bardzo wysoki'],
              ['Display Remarketing', 'Banery na stronach partnerskich Google dla byłych odwiedzających', 'Wysoki'],
              ['Dynamic Remarketing', 'Automatyczne reklamy z produktami, które użytkownik oglądał', 'Najwyższy (e-commerce)'],
              ['YouTube Remarketing', 'Reklamy video dla osób, które odwiedziły stronę', 'Średni'],
              ['Customer Match', 'Targetowanie po emailach klientów (lista importowana)', 'Wysoki'],
            ].map(([type, desc, roas], i) => (
              <Table.Tr key={i}>
                <Table.Td><Text size="sm" fw={500}>{type}</Text></Table.Td>
                <Table.Td><Text size="sm">{desc}</Text></Table.Td>
                <Table.Td><Badge size="xs" variant="light" color="green">{roas}</Badge></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Setup steps */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Konfiguracja remarketingu krok po kroku</Text>
        <Stepper active={-1} size="sm" color="brand" orientation="vertical">
          <Stepper.Step label="Zainstaluj tag Google Ads" description="Taki sam tag jak dla konwersji (gtag.js). Jeśli już masz tag konwersji, ten krok jest zrobiony." />
          <Stepper.Step label="Utwórz listę odbiorców" description='Google Ads > Narzędzia > Menedżer odbiorców > + Nowy segment. Wybierz "Odwiedzający witrynę".' />
          <Stepper.Step label="Poczekaj na zebranie danych" description="Lista musi mieć minimum 100 użytkowników (Search) lub 1000 (Display). Zazwyczaj 1-4 tygodnie." />
          <Stepper.Step label="Utwórz kampanię remarketingową" description="Nowa kampania Display lub dodaj listę odbiorców do istniejącej kampanii Search (RLSA)." />
          <Stepper.Step label="Ustaw budżet i stawki" description="Remarketing ma niższy CPC. Zacznij od 20-30 zł/dzień. Stawki mogą być niższe niż w zwykłych kampaniach." />
        </Stepper>
      </Paper>

      {/* Audience segments */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Rekomendowane segmenty odbiorców</Text>
        <Stack gap="md">
          {[
            { icon: Eye, title: 'Wszyscy odwiedzający (30 dni)', desc: 'Podstawowa lista, szeroki zasięg', tip: 'Dobry punkt startowy, niski CPC' },
            { icon: ShoppingCart, title: 'Porzucony koszyk (14 dni)', desc: 'Osoby, które dodały do koszyka ale nie kupiły', tip: 'Najwyższy ROAS, dynamiczne reklamy z produktami' },
            { icon: Users, title: 'Klienci (180 dni)', desc: 'Osoby, które już kupiły', tip: 'Cross-sell, upsell, nowe kolekcje' },
            { icon: Globe, title: 'Odwiedzający konkretne strony', desc: 'Np. strona cennika, kontaktu, kategorii', tip: 'Precyzyjne targetowanie, dopasowana treść' },
          ].map(({ icon, title, desc, tip }, i) => {
            const Icon = icon;
  const guidesBase = useGuidesBase();
            return (
              <Group key={i} gap="sm" wrap="nowrap" align="flex-start">
                <ThemeIcon variant="light" color="brand" size="md" radius="md">
                  <Icon size={16} weight="bold" />
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>{title}</Text>
                  <Text size="xs" c="dimmed">{desc}</Text>
                  <Text size="xs" c="green.7" mt={2}>{tip}</Text>
                </Box>
              </Group>
            );
          })}
        </Stack>
      </Paper>

      {/* Best practices */}
      <Paper withBorder p="lg" radius="md">
        <Text size="sm" fw={600} mb="sm">Najlepsze praktyki</Text>
        <Stack gap="xs">
          {[
            'Nie targetuj wszystkich odwiedzających tą samą reklamą. Segmentuj po zachowaniu',
            'Ustaw limit częstotliwości (frequency cap): max 3-5 wyświetleń/użytkownika/dzień',
            'Wyklucz osoby, które już dokonały konwersji (chyba że robisz cross-sell)',
            'Dynamic remarketing wymaga feeda produktowego (Google Merchant Center)',
            'RLSA: zwiększ stawki o 20-50% dla powracających użytkowników w kampaniach Search',
            'Testuj różne okresy list: 7 dni (gorący), 30 dni (ciepły), 90 dni (zimny)',
            'Dopasuj treść reklamy do etapu lejka (np. "Dokończ zakup" vs "Nowa kolekcja")',
          ].map((tip, i) => (
            <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color="green" size="xs" radius="xl"><Check size={10} weight="bold" /></ThemeIcon>
              <Text size="sm" lh={1.5}>{tip}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
